import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AppCheck } from '@angular/fire/app-check';
import { withScope, captureException } from '@sentry/angular';
import { onTokenChanged } from 'firebase/app-check';

type AppCheckFlow = 'Auth' | 'Firestore' | 'TokenRefresh';

interface AppCheckReportContext {
  operation?: string;
  schoolId?: string;
  assumeAppCheck?: boolean;
}

interface AppCheckClassification {
  category: 'recaptcha-error' | 'token-fetch-error' | 'appcheck-permission-denied' | 'invalid-app-credential';
  code: string;
  userMessage: string;
  level: 'warning' | 'error';
}

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  name?: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class AppCheckErrorService {
  private router = inject(Router);
  private appCheck = inject(AppCheck);

  private readonly dedupeWindowMs = 60_000;
  private readonly recentlySent = new Map<string, number>();
  private unsubscribeTokenListener: (() => void) | null = null;
  private tokenMonitoringStarted = false;

  public readonly errorMessage = signal<string | null>(null);

  startTokenMonitoring(): void {
    if (this.tokenMonitoringStarted) {
      return;
    }

    try {
      this.unsubscribeTokenListener = onTokenChanged(
        this.appCheck,
        () => {
          // No-op for successful token changes.
        },
        (error) => {
          this.reportIfAppCheckError(error, 'TokenRefresh', { operation: 'onTokenChanged' });
        }
      );
      this.tokenMonitoringStarted = true;
    } catch (error) {
      // Keep bootstrap resilient; App Check may not be initialized yet.
      console.warn('Unable to start App Check token monitoring.', error);
    }
  }

  reportIfAppCheckError(error: unknown, flow: AppCheckFlow, context: AppCheckReportContext = {}): boolean {
    const classification = this.classify(error, flow, context);
    if (!classification) {
      return false;
    }

    this.errorMessage.set(classification.userMessage);

    const dedupeKey = `${flow}:${classification.category}:${classification.code}`;
    if (!this.shouldReportToSentry(dedupeKey)) {
      return true;
    }

    withScope(scope => {
      scope.setTag('Firebase', 'AppCheck');
      scope.setTag('AppCheckFlow', flow);
      scope.setTag('AppCheckCode', classification.code);
      scope.setTag('AppCheckCategory', classification.category);
      scope.setLevel(classification.level);
      scope.setFingerprint(['appcheck-enforcement', flow, classification.category, classification.code]);
      scope.setExtra('route', this.router.url);
      scope.setExtra('operation', context.operation || 'unknown');
      scope.setExtra('schoolId', context.schoolId || 'unknown');
      scope.setExtra('errorMessage', this.readString((error as ErrorLike)?.message));
      captureException(error);
    });

    return true;
  }

  clearErrorMessage(): void {
    this.errorMessage.set(null);
  }

  private classify(error: unknown, flow: AppCheckFlow, context: AppCheckReportContext): AppCheckClassification | null {
    const code = this.readString((error as ErrorLike)?.code).toLowerCase();
    const message = this.readString((error as ErrorLike)?.message).toLowerCase();
    const combined = `${code} ${message}`;
    const hasAppCheckHint = combined.includes('appcheck') || combined.includes('recaptcha');

    if (combined.includes('appcheck/recaptcha-error') || message.includes('recaptcha')) {
      return {
        category: 'recaptcha-error',
        code: code || 'appcheck/recaptcha-error',
        userMessage: 'Security verification failed. Please refresh and try again.',
        level: 'warning'
      };
    }

    if (combined.includes('appcheck/fetch-status-error') || combined.includes('appcheck/fetch-network-error')) {
      return {
        category: 'token-fetch-error',
        code: code || 'appcheck/fetch-status-error',
        userMessage: 'Could not verify this session right now. Please check your connection and try again.',
        level: 'warning'
      };
    }

    if (flow === 'Auth' && (
      combined.includes('invalid-app-credential')
      || combined.includes('firebase-app-check-token-is-invalid')
      || combined.includes('app-check-token-is-invalid')
    )) {
      return {
        category: 'invalid-app-credential',
        code: code || 'auth/invalid-app-credential',
        userMessage: 'Security verification failed during sign in. Please refresh and try again.',
        level: 'error'
      };
    }

    const isPermissionDenied = combined.includes('permission-denied') || message.includes('missing or insufficient permissions');
    if (flow === 'Firestore' && isPermissionDenied && (hasAppCheckHint || context.assumeAppCheck)) {
      return {
        category: 'appcheck-permission-denied',
        code: code || 'firestore/permission-denied',
        userMessage: 'We could not verify your app session. Please refresh and try again.',
        level: 'error'
      };
    }

    return null;
  }

  private shouldReportToSentry(key: string): boolean {
    const now = Date.now();
    const lastSeen = this.recentlySent.get(key);
    if (lastSeen && now - lastSeen < this.dedupeWindowMs) {
      return false;
    }
    this.recentlySent.set(key, now);
    return true;
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}
